import React, { useState, useEffect } from 'react';
import * as S from './TemplatesPage.styles';
import { getTemplates, createTemplate, updateTemplate, deleteTemplate, Template } from '../../entities/template/api/templateApi';

export const TemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    content: '',
    category: '',
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await getTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Ошибка загрузки шаблонов:', error);
      alert('Не удалось загрузить шаблоны');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTemplate = () => {
    setEditingTemplate(null);
    setFormData({ name: '', content: '', category: '' });
    setShowAddTemplate(true);
  };

  const handleEditTemplate = (template: Template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      content: template.content,
      category: template.category || '',
    });
    setShowAddTemplate(true);
  };

  const handleSaveTemplate = async () => {
    if (!formData.name || !formData.content) {
      alert('Заполните все обязательные поля');
      return;
    }

    setLoading(true);
    try {
      if (editingTemplate) {
        await updateTemplate(editingTemplate.id, {
          name: formData.name,
          content: formData.content,
          category: formData.category || undefined,
        });
      } else {
        await createTemplate({
          name: formData.name,
          content: formData.content,
          category: formData.category || undefined,
        });
      }
      await loadTemplates();
      setShowAddTemplate(false);
      setFormData({ name: '', content: '', category: '' });
    } catch (error) {
      console.error('Ошибка сохранения шаблона:', error);
      alert('Не удалось сохранить шаблон');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    if (window.confirm('Удалить шаблон?')) {
      setLoading(true);
      try {
        await deleteTemplate(id);
        await loadTemplates();
      } catch (error) {
        console.error('Ошибка удаления шаблона:', error);
        alert('Не удалось удалить шаблон');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <S.TemplatesContainer>
      <S.TemplatesHeader>
        <S.TemplatesTitle>Шаблоны</S.TemplatesTitle>
        <S.AddButton onClick={handleAddTemplate}>
          + Добавить шаблон
        </S.AddButton>
      </S.TemplatesHeader>

      <S.TemplatesContent>
        <S.Section>
          <S.SectionTitle>Шаблоны ответов</S.SectionTitle>
          
          {loading && templates.length === 0 ? (
            <S.EmptyState>
              <S.EmptyStateText>Загрузка...</S.EmptyStateText>
            </S.EmptyState>
          ) : templates.length === 0 ? (
            <S.EmptyState>
              <S.EmptyStateText>Нет шаблонов</S.EmptyStateText>
              <S.EmptyStateSubtext>
                Создайте шаблоны для быстрых ответов
              </S.EmptyStateSubtext>
            </S.EmptyState>
          ) : (
            <S.TemplatesList>
              {templates.map(template => (
                <S.TemplateCard key={template.id}>
                  <S.TemplateCardHeader>
                    <S.TemplateCardName>{template.name}</S.TemplateCardName>
                    {template.category && (
                      <S.TemplateCardCategory>{template.category}</S.TemplateCardCategory>
                    )}
                  </S.TemplateCardHeader>
                  <S.TemplateCardContent>
                    {template.content.substring(0, 150)}
                    {template.content.length > 150 && '...'}
                  </S.TemplateCardContent>
                  <S.TemplateCardActions>
                    <S.EditButton onClick={() => handleEditTemplate(template)}>
                      Редактировать
                    </S.EditButton>
                    <S.DeleteButton onClick={() => handleDeleteTemplate(template.id)}>
                      Удалить
                    </S.DeleteButton>
                  </S.TemplateCardActions>
                </S.TemplateCard>
              ))}
            </S.TemplatesList>
          )}
        </S.Section>
      </S.TemplatesContent>

      {showAddTemplate && (
        <S.ModalOverlay onClick={() => setShowAddTemplate(false)}>
          <S.ModalContent onClick={(e) => e.stopPropagation()}>
            <S.ModalHeader>
              <S.ModalTitle>
                {editingTemplate ? 'Редактировать шаблон' : 'Создать шаблон'}
              </S.ModalTitle>
              <S.CloseButton onClick={() => setShowAddTemplate(false)}>×</S.CloseButton>
            </S.ModalHeader>

            <S.ModalBody>
              <S.FormGroup>
                <S.FormLabel>Название *</S.FormLabel>
                <S.FormInput
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Название шаблона"
                />
              </S.FormGroup>

              <S.FormGroup>
                <S.FormLabel>Категория</S.FormLabel>
                <S.FormInput
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="Категория (опционально)"
                />
              </S.FormGroup>

              <S.FormGroup>
                <S.FormLabel>Содержимое *</S.FormLabel>
                <S.FormTextarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Текст шаблона..."
                  rows={10}
                />
              </S.FormGroup>
            </S.ModalBody>

            <S.ModalFooter>
              <S.CancelButton onClick={() => setShowAddTemplate(false)}>
                Отмена
              </S.CancelButton>
              <S.SaveButton onClick={handleSaveTemplate} disabled={loading}>
                {loading ? 'Сохранение...' : 'Сохранить'}
              </S.SaveButton>
            </S.ModalFooter>
          </S.ModalContent>
        </S.ModalOverlay>
      )}
    </S.TemplatesContainer>
  );
};

